import React, { useState } from 'react';
import type { CreateVersionPayload, FileItem } from '../types';

interface Props {
  file: FileItem;
  onClose: () => void;
  onSubmit: (payload: CreateVersionPayload) => Promise<void>;
}

export const NewVersionModal: React.FC<Props> = ({ file, onClose, onSubmit }) => {
  const [versionNo, setVersionNo] = useState('v2026.09');
  const [changeNote, setChangeNote] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('2026-09-01');
  const [source, setSource] = useState('internal');
  const [contentPreview, setContentPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeNote.trim()) {
      setErrorMsg('Описание изменений обязательно для создания новой редакции документа.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      await onSubmit({
        version_no: versionNo,
        change_note: changeNote,
        effective_date: effectiveDate,
        source,
        content_preview: contentPreview || undefined,
      });
      onClose();
    } catch {
      setErrorMsg('Ошибка при создании новой версии документа.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="new-version-modal">
      <div className="modal-window" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px' }}>
        <div className="modal-header">
          <div>
            <h3>Новая версия документа</h3>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              {file.display_name} (текущая: {file.current_version_no})
            </span>
          </div>
          <button type="button" className="file-hub-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {errorMsg && (
              <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', color: '#f87171', padding: '0.65rem 0.85rem', borderRadius: '8px', fontSize: '0.85rem' }}>
                {errorMsg}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
                  Номер версии (vX.Y)
                </label>
                <input
                  type="text"
                  className="library-search-input"
                  style={{ width: '100%' }}
                  value={versionNo}
                  onChange={(e) => setVersionNo(e.target.value)}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
                  Дата вступления в силу
                </label>
                <input
                  type="date"
                  className="library-search-input"
                  style={{ width: '100%' }}
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
                Источник изменений
              </label>
              <select
                className="library-select"
                style={{ width: '100%' }}
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="uploaded">Загрузка файла (uploaded)</option>
                <option value="internal">Внутренняя доработка (internal)</option>
                <option value="generated">Сгенерировано системой (generated)</option>
                <option value="imported">Импортировано (imported)</option>
                <option value="reference">Официальный внешний первоисточник (reference)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
                Описание изменений (Change Note, обязательно)
              </label>
              <textarea
                className="library-search-input"
                style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="Укажите причину и суть внесённых изменений..."
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
                Обновлённое содержимое или превью
              </label>
              <textarea
                className="library-search-input"
                style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                value={contentPreview}
                onChange={(e) => setContentPreview(e.target.value)}
                placeholder="Вставьте фрагмент обновлённого текста..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="file-hub-action-btn" onClick={onClose}>
              Отмена
            </button>
            <button
              type="submit"
              className="btn-open-preview"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Сохранение...' : 'Зафиксировать редакцию'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
